import {
  RELAY_FULL_CONTROL_SCOPES,
  RELAY_REMOTE_CONTROL_MIN_VERSION,
  RELAY_PROTOCOL_VERSION,
  type CmmdDeviceRegistrySnapshot,
  type RelayActionGrantRequest,
  type RegisterCmmdDeviceParams,
} from "@cmmd-forge/contracts";
import {
  createRelayControlEndpoint,
  RelayControlRouteSecurityFailure,
  relayControlEndpointRequest,
  secureRelayControlRoute,
  type SecuredRelayControlRoute,
} from "@cmmd-forge/client-runtime";

import type { CmmdApiClientInterface } from "../cmmdApi";

type RelayControlRouteApi = Pick<
  CmmdApiClientInterface,
  "getDeviceRegistry" | "registerDevice" | "issueRelayControlRoute" | "issueRelayActionGrant"
>;

class RelayControlRouteError extends Error {
  readonly code:
    | "FORGE_CONTROL_DEVICE_REVOKED"
    | "FORGE_CONTROL_DEVICE_UNAUTHORIZED"
    | "FORGE_REMOTE_CONTROL_NOT_ENABLED"
    | "FORGE_RUNTIME_UPDATE_REQUIRED";

  constructor(code: RelayControlRouteError["code"], message: string) {
    super(message);
    this.name = "RelayControlRouteError";
    this.code = code;
  }
}

function translateRelayRouteError(error: unknown): never {
  const code =
    error && typeof error === "object" ? (error as { readonly code?: unknown }).code : null;
  if (code === "PROTOCOL_INCOMPATIBLE") {
    throw new RelayControlRouteError(
      "FORGE_RUNTIME_UPDATE_REQUIRED",
      "Update Forge on the target runtime before connecting remotely.",
    );
  }
  if (code === "PROTOCOL_ROLLOUT_DENIED") {
    throw new RelayControlRouteError(
      "FORGE_REMOTE_CONTROL_NOT_ENABLED",
      "Remote control is not enabled for this CMMD account. Synced runtime inventory remains available.",
    );
  }
  throw error;
}

interface ControlRegistryIdentity {
  readonly deviceKey: string | null;
  readonly registry: CmmdDeviceRegistrySnapshot;
}

async function loadControlRegistryIdentity(
  dependencies: RelayControlRouteProviderDependencies,
): Promise<ControlRegistryIdentity> {
  let deviceKey = dependencies.getStoredDeviceKey();
  let registry = deviceKey
    ? await dependencies.apiClient.getDeviceRegistry()
    : await dependencies.apiClient.registerDevice(await dependencies.buildDeviceRegistration());
  if (deviceKey && !registry.devices.some((device) => device.deviceKey === deviceKey)) {
    registry = await dependencies.apiClient.registerDevice(
      await dependencies.buildDeviceRegistration(),
    );
  }
  deviceKey = deviceKey ?? dependencies.getStoredDeviceKey();
  return { deviceKey, registry };
}

function requireTrustedControlDevice(identity: ControlRegistryIdentity) {
  if (!identity.deviceKey) {
    throw new RelayControlRouteError(
      "FORGE_CONTROL_DEVICE_UNAUTHORIZED",
      "This Forge control surface could not establish CMMD identity.",
    );
  }

  const device = identity.registry.devices.find(
    (candidate) => candidate.deviceKey === identity.deviceKey,
  );
  if (device?.trustState === "revoked" || device?.authorization?.revoked === true) {
    throw new RelayControlRouteError(
      "FORGE_CONTROL_DEVICE_REVOKED",
      "This Forge control surface was revoked by CMMD.",
    );
  }
  if (!device || device.trustState !== "trusted") {
    throw new RelayControlRouteError(
      "FORGE_CONTROL_DEVICE_UNAUTHORIZED",
      "This Forge control surface is not trusted by CMMD.",
    );
  }
  return device;
}

export interface RelayControlRouteProviderDependencies {
  readonly apiClient: RelayControlRouteApi;
  readonly getStoredDeviceKey: () => string | null;
  readonly buildDeviceRegistration: () => Promise<RegisterCmmdDeviceParams>;
  readonly controlSessionId: string;
}

export interface RelayControlRouteProvider {
  readonly acquireRoute: () => Promise<SecuredRelayControlRoute>;
  readonly acquireActionGrant: (request: RelayActionGrantRequest) => Promise<string>;
}

export type { SecuredRelayControlRoute } from "@cmmd-forge/client-runtime";

export function createRelayControlRouteProvider(
  runtimeId: string,
  dependencies: RelayControlRouteProviderDependencies,
): RelayControlRouteProvider {
  const trustedDevice = async () => {
    const identity = await loadControlRegistryIdentity(dependencies);
    return requireTrustedControlDevice(identity);
  };
  return {
    acquireRoute: async () => {
      const device = await trustedDevice();
      const endpoint = await createRelayControlEndpoint();
      try {
        const route = await dependencies.apiClient.issueRelayControlRoute(runtimeId, {
          controlDeviceId: device.id,
          controlSessionId: dependencies.controlSessionId,
          scopes: [...RELAY_FULL_CONTROL_SCOPES],
          protocolMinVersion: RELAY_REMOTE_CONTROL_MIN_VERSION,
          protocolMaxVersion: RELAY_PROTOCOL_VERSION,
          ...relayControlEndpointRequest(endpoint),
        });
        return secureRelayControlRoute({ route, runtimeId, endpoint });
      } catch (error) {
        if (error instanceof RelayControlRouteSecurityFailure) {
          throw new RelayControlRouteError("FORGE_RUNTIME_UPDATE_REQUIRED", error.message);
        }
        return translateRelayRouteError(error);
      }
    },
    acquireActionGrant: async (request) => {
      const device = await trustedDevice();
      try {
        const grant = await dependencies.apiClient.issueRelayActionGrant(runtimeId, {
          controlDeviceId: device.id,
          controlSessionId: dependencies.controlSessionId,
          ...request,
        });
        return grant.token;
      } catch (error) {
        return translateRelayRouteError(error);
      }
    },
  };
}
